// User-curated "pretty neighborhood" nominations. Augments the hardcoded
// PRETTY_NEIGHBORHOODS catalog so the walking-mode score reflects what
// real users on the ground vote up — not just what we put in code.

import { supabase } from "@/integrations/supabase/client";
import type { LngLat } from "@/utils/polyline";
import { haversine } from "@/utils/polyline";

export interface Nomination {
  id: string;
  user_id: string;
  name: string;
  lat: number;
  lng: number;
  tier: 1 | 2 | 3;
  why: string | null;
  created_at: string;
  /** Aggregated count of upvotes (excluding the implicit nominator vote). */
  votes?: number;
}

export async function submitNomination(args: {
  name: string;
  coord: LngLat;
  tier: 1 | 2 | 3;
  why?: string;
}): Promise<Nomination> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to nominate a neighborhood");

  const { data, error } = await supabase
    .from("nominated_neighborhoods")
    .insert({
      user_id: user.id,
      name: args.name.trim().slice(0, 80),
      lng: args.coord[0],
      lat: args.coord[1],
      tier: args.tier,
      why: args.why?.trim().slice(0, 200) || null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as Nomination;
}

export async function upvoteNomination(nominationId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to upvote");
  const { error } = await supabase
    .from("neighborhood_votes")
    .insert({ nomination_id: nominationId, user_id: user.id });
  // Ignore duplicate-key errors (already voted).
  if (error && !/duplicate/i.test(error.message)) throw error;
}

/** Fetch nominations near a route bbox, with vote counts. Used by scoring. */
export async function fetchNominationsNear(
  bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number },
): Promise<Nomination[]> {
  const { data, error } = await supabase
    .from("nominated_neighborhoods")
    .select("id, user_id, name, lat, lng, tier, why, created_at")
    .gte("lat", bbox.minLat)
    .lte("lat", bbox.maxLat)
    .gte("lng", bbox.minLng)
    .lte("lng", bbox.maxLng)
    .limit(200);
  if (error) return [];
  const noms = (data ?? []) as Nomination[];
  if (noms.length === 0) return [];
  // Pull vote counts in one round-trip.
  const { data: votes } = await supabase
    .from("neighborhood_votes")
    .select("nomination_id")
    .in("nomination_id", noms.map((n) => n.id));
  const counts = new Map<string, number>();
  for (const v of (votes ?? []) as { nomination_id: string }[]) {
    counts.set(v.nomination_id, (counts.get(v.nomination_id) ?? 0) + 1);
  }
  return noms.map((n) => ({ ...n, votes: counts.get(n.id) ?? 0 }));
}

/** Coverage of a route polyline by user-nominated neighborhoods. Mirrors
 *  prettyNeighborhoodCoverage() so scoring can sum the two. Tier is bumped
 *  by upvote count (>=3 votes promotes a tier-1 to effective tier 2). */
export async function nominatedNeighborhoodCoverage(
  coords: LngLat[],
): Promise<{ fraction: number; weighted: number; names: string[] }> {
  if (coords.length < 2) return { fraction: 0, weighted: 0, names: [] };
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  // Pad bbox by ~500m so a nomination centroid just outside still counts.
  const pad = 0.005;
  const noms = await fetchNominationsNear({
    minLng: minLng - pad, maxLng: maxLng + pad,
    minLat: minLat - pad, maxLat: maxLat + pad,
  });
  if (noms.length === 0) return { fraction: 0, weighted: 0, names: [] };
  // Each nomination acts like a 350m radius circle.
  const RADIUS_M = 350;
  let totalM = 0, insideM = 0, weightedM = 0;
  const names = new Set<string>();
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1], b = coords[i];
    const segLen = haversine(a, b);
    totalM += segLen;
    const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    let bestTier = 0, bestName: string | undefined;
    for (const n of noms) {
      if (haversine(mid, [n.lng, n.lat]) <= RADIUS_M) {
        // Vote-weighted effective tier: +1 per 3 upvotes, capped at 3.
        const eff = Math.min(3, n.tier + Math.floor((n.votes ?? 0) / 3));
        if (eff > bestTier) { bestTier = eff; bestName = n.name; }
      }
    }
    if (bestTier > 0) {
      insideM += segLen;
      weightedM += segLen * bestTier;
      if (bestName) names.add(bestName);
    }
  }
  if (totalM === 0) return { fraction: 0, weighted: 0, names: [] };
  return { fraction: insideM / totalM, weighted: weightedM / (totalM * 3), names: Array.from(names) };
}

// ─────────── Sync cache for scoring ───────────
// Scoring is synchronous, so we keep the most recent fetched nominations in
// a module-level cache. `prefetchNominationsForRoutes` warms it; scoring
// then reads via `cachedNominationsAsPretty()` and merges them into the
// hardcoded PRETTY_NEIGHBORHOODS catalog.

import type { PrettyNeighborhood } from "@/utils/prettyNeighborhoods";

let cache: Nomination[] = [];
let cacheKey = "";

export async function prefetchNominationsForRoutes(allCoords: LngLat[]): Promise<void> {
  if (allCoords.length < 2) return;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of allCoords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const pad = 0.02;
  const key = `${minLng.toFixed(2)},${minLat.toFixed(2)},${maxLng.toFixed(2)},${maxLat.toFixed(2)}`;
  if (key === cacheKey) return;
  cacheKey = key;
  const noms = await fetchNominationsNear({
    minLng: minLng - pad, maxLng: maxLng + pad,
    minLat: minLat - pad, maxLat: maxLat + pad,
  });
  cache = noms;
}

/** Return cached nominations shaped as PrettyNeighborhood for scoring.
 *  Vote-weighted effective tier (+1 tier per 3 upvotes, capped at 3). */
export function cachedNominationsAsPretty(): PrettyNeighborhood[] {
  return cache.map((n) => {
    const eff = Math.min(3, n.tier + Math.floor((n.votes ?? 0) / 3)) as 1 | 2 | 3;
    return {
      name: n.name,
      city: "user",
      coord: [n.lng, n.lat] as LngLat,
      radiusM: 350,
      tier: eff,
    };
  });
}
